/* import __COLOCATED_TEMPLATE__ from './lightbox.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { allSettled, dropTask, restartableTask } from 'ember-concurrency';
import { PROFILE_PROJECTDIGEST_INCLUDES } from 'sh-www/constants/components/profile';
import { FS_EVENTS } from 'sh-www/constants/datalayer';
import { VERIFICATION_OVERLAY_FORM_INPUTS } from 'sh-www/components/modals/overlays/lightbox/verification-screen';
import { COMPANY } from 'sh-www/models/company';
import { PROJECTDIGEST_VERIFICATION_STATUS } from 'sh-www/models/projectdigest-verification';
import { IMAGE_COMMANDS__MODEL_TYPES } from 'sh-www/services/commands/image-commands';
import { storeDataOnEvent } from 'sh-www/utils/datalayer';
import { handleFormErrorInOverlay } from 'sh-www/utils/handles-errors';
import { htmlEntitiesDecode } from 'sh-www/utils/misc';
import { closeModal } from 'sh-www/utils/modal-controls';
import FocusTrapModifier from 'sh-www/modifiers/focus-trap';
import NoopModifier from 'sh-www/modifiers/noop';
import clsx from 'clsx';

const CLASS_NAME = 'GLM-c-modals-overlays-lightbox';

const SECONDARY_SCREENS = {
  EDIT: 'edit',
  VERIFY: 'verify',
  DELETE: 'delete',
};

class GlmLightboxComponent extends Component {
  @service('state-managers/application') applicationStateManager;
  @service errorTracker;
  @service('social/facebook-provider') facebookProviderService;
  @service('commands/image-commands') imageCommandService;
  @service('commands/projectdigest-commands') projectdigestCommandService;
  @service media;
  @service session;
  @service shData;
  @service store;

  @tracked editScreenErrors = {};
  @tracked verifyScreenErrors = {};
  @tracked deleteScreenErrors = {}
  @tracked secondaryScreen = this.args.model.screen ?? null;
  @tracked selectedProjectDigestVideoCarouselIndex = this.args.model.selectedProjectDigestVideoCarouselIndex;
  @tracked facebookSDKInitialized = false;
  @tracked disableFacebookPlayer = false;

  get classNames() {
    return clsx(
      this.args.class,
      CLASS_NAME,
    );
  }

  get className() {
    return CLASS_NAME;
  }

  get mediaWrapperClasses() {
    const THEATER_COLUMN_CLASS_NAME = `${CLASS_NAME}__theater-column`;

    return clsx(
      this.media.isLargeAndUp && THEATER_COLUMN_CLASS_NAME,
      this.media.isLargeAndUp && `${THEATER_COLUMN_CLASS_NAME}--left`,
    );
  }

  get carouselNavigationHidden() {
    return this.selectedProjectDigestVideoCarouselIndex === undefined
      || this.args.model.digestsToShowForVideoCarousel.length === 1;
  }

   /**
   * @type {Boolean}                  True when we will show the left arrow.
   */
  get carouselNavigatePreviousDisabled() {
    return this.selectedProjectDigestVideoCarouselIndex === 0;
  }

  /**
   * @type {Boolean}                  True when we will show the right arrow.
   */
  get carouselNavigateNextDisabled() {
    return this.selectedProjectDigestVideoCarouselIndex >= (this.args.model.digestsToShowForVideoCarousel.length - 1);
  }

  get closeOverlayDisabled() {
    return this.handleSaveProjectdigestTask.isRunning;
  }

  get companyName() {
    const { selectedDigest } = this;
    const name = selectedDigest.brand?.displayedName ?? selectedDigest.company.name;

    return htmlEntitiesDecode(name);
  }

  get projectDescription() {
    return htmlEntitiesDecode(this.selectedDigest.description ?? '');
  }

  get companyImageSrc() {
    const { selectedDigest } = this;
    return selectedDigest.brand
      ? selectedDigest.brand.image.transforms.profile128
      : selectedDigest.company.image.transforms.profile128;
  }

  get companyVerified() {
    const { company } = this.selectedDigest;
    return company && company.type !== COMPANY.TYPES.SHELL;
  }

  get isSellerViewingSelf() {
    const { currentUser } = this.session;
    return currentUser?.isSeller && currentUser?.id === this.selectedDigest.user?.id;
  }

  get isWorkSampleVerified() {
    return this.selectedDigest.confirm
      || this.selectedDigest.mostRecentProjectDigestVerification?.status === PROJECTDIGEST_VERIFICATION_STATUS.VERIFIED;
  }

  get needsVerification() {
    return !this.companyVerified && !this.isWorkSampleVerified;
  }

  get canShowVerificationContent() {
    return this.isSellerViewingSelf || !this.needsVerification;
  }

  get screenHasFocusTrap() {
    return this.secondaryScreen
      ? FocusTrapModifier
      : NoopModifier;
  }

  get selectedDigest() {
    const selectedDigestIndex =  this.selectedProjectDigestVideoCarouselIndex ?? 0;
    const projectDigests = this.args.model.digestsToShowForVideoCarousel;

    if (!projectDigests) {
      return null;
    }

    return projectDigests[selectedDigestIndex];
  }

  get onEditScreen() {
    return this.secondaryScreen === SECONDARY_SCREENS.EDIT;
  }

  get onVerifyScreen() {
    return this.secondaryScreen === SECONDARY_SCREENS.VERIFY;
  }

  get onDeleteScreen() {
    return this.secondaryScreen === SECONDARY_SCREENS.DELETE;
  }

  @action
  initFacebookVideoService() {
    // wait to init until necessary
    if (!this.selectedDigest.oembed?.isFacebookVideo) {
      return;
    }

    this.loadFacebookPlayerSDK.perform();
  }

  @action
  handleCloseSecondaryScreen() {
    this.secondaryScreen = null;
    this.args.model.closeSecondaryScreen?.();
  }

  @action
  clearErrors(errorKey) {
    this[errorKey] = {};
  }

  @action
  setErrors(errorKey, values) {
    this[errorKey] = values;
  }

  @action
  updateError(errorKey, key, value) {
    this[errorKey] = { ...this[errorKey], [key]: value };
  }

  @action
  handleKeyUp(e) {
    if (e.defaultPrevented) {
      return;
    }

    // enable standard accessibility
    if (e.key === 'Escape' && !this.closeOverlayDisabled) {
      e.preventDefault();

      // close experience within overlay first
      if (this.secondaryScreen) {
        this.secondaryScreen = null;
      } else {
        closeModal(this);
      }
    }
  }

  @action
  onCarouselNavigatePrevious() {
    if (this.carouselNavigatePreviousDisabled) {
      return;
    }

    const digestIndex = this.selectedProjectDigestVideoCarouselIndex;
    const digests = this.args.model.digestsToShowForVideoCarousel;
    const nextDigest = digests[digestIndex - 1];

    this.args.model.handleNewFeaturedDigestSelected(nextDigest, digestIndex - 1);
    this.selectedProjectDigestVideoCarouselIndex = digestIndex - 1;
  }

  @action
  onCarouselNavigateNext() {
    if (this.carouselNavigateNextDisabled) {
      return;
    }

    const digestIndex = this.selectedProjectDigestVideoCarouselIndex;
    const digests = this.args.model.digestsToShowForVideoCarousel;
    const nextDigest = digests[digestIndex + 1];

    this.args.model.handleNewFeaturedDigestSelected(nextDigest, digestIndex + 1);
    this.selectedProjectDigestVideoCarouselIndex = digestIndex + 1;
  }

  @action
  onClickLinkedVideo(selectedDigest) {
    if (!selectedDigest.isLinkableVideo) {
      return;
    }

    storeDataOnEvent(
      FS_EVENTS.PROJECTDIGEST_LINK_CLICKED,
      this.session.currentUser?.id,
      {
        page: window.location.href,
        id: selectedDigest.id,
        title: selectedDigest.title,
      },
    );

    window.open(selectedDigest.link, '_blank');
  }

  @action
  onCloseOverlayClicked() {
    closeModal(this);
  }

  @action
  onAddVerificationClicked() {
    this.secondaryScreen = SECONDARY_SCREENS.VERIFY;
  }

  @action
  onEditClicked() {
    this.secondaryScreen = SECONDARY_SCREENS.EDIT;
  }

  @action
  onDeleteClicked() {
    this.secondaryScreen = SECONDARY_SCREENS.DELETE;
  }

  @dropTask
  *handleDeleteProjectdigestTask(projectdigest) {
    this.deleteScreenErrors = {};

    try {
      yield this.projectdigestCommandService.deleteProjectDigest(projectdigest, false);
      projectdigest.deleteRecord();
      this.applicationStateManager.addToast('Project deleted');
      this.args.model.handleDeleteProjectdigest?.perform(projectdigest);
    } catch (error) {
      this.errorTracker.error(`handleDeleteProjectdigestTask - failed to delete projectdigest id: ${projectdigest.id}`, this, error);
      this.deleteScreenErrors = { ...this.deleteScreenErrors, 'generic-exception': 'Failed to delete this project. Please try again.' };
    }

    closeModal(this);
  }

  @restartableTask
  *handleSaveProjectdigestTask(projectdigest, {
    classifications,
    description,
    image,
    title,
    link,
    services,
  }) {
    const updateDigestDataPromise = this.projectdigestCommandService.updateProjectDigest(
      this.session.currentUser,
      projectdigest,
      {
        title,
        description,
        link,
        relationships: {
          services: {
            data: services.map(({ id }) => ({ id, type: 'services' })),
          },
        },
      },
    );

    const currentClassificationIds = projectdigest.classifications.map(({ id }) => id);
    const newClassificationIds = classifications.map(({ model }) => model);
    const areClassificationsEqual = (arr1, arr2) => {
      return arr1.length === arr2.length && arr1.every((value, index) => value === arr2[index]);
    };

    // update classifications if anything changed
    const updateClassificationsPromise = areClassificationsEqual(currentClassificationIds, newClassificationIds)
      ? Promise.resolve()
      : this.projectdigestCommandService.updateProjectdigestClassifications(
        projectdigest.id,
        newClassificationIds,
      );

    // custom thumbnail
    const updateCustomThumbnailPromise = image
      ? this.imageCommandService.saveNewImageForModel(
          IMAGE_COMMANDS__MODEL_TYPES.PROJECT_DIGEST,
          projectdigest.id,
          image.width,
          image.height,
          image.cloudId,
          image.cloudVersion,
          image.type,
        )
      : Promise.resolve();

    const [
      updateDigestDataResponse,
      updateClassificationsResponse,
      updateCustomThumbnailResponse,
    ] = yield allSettled([
      updateDigestDataPromise,
      updateClassificationsPromise,
      updateCustomThumbnailPromise
    ]);

    const errorMessages = [];
    if (updateDigestDataResponse.state === 'rejected') {
      errorMessages.push('Failed to update work sample.');
    }

    if (updateClassificationsResponse.state === 'rejected') {
      errorMessages.push('Failed to save work sample classification.');
    }

    if (updateCustomThumbnailResponse.state === 'rejected') {
      errorMessages.push('Failed to update thumbnail.');
    }

    if (errorMessages.length > 0) {
      this.editScreenErrors = { ...this.editScreenErrors, 'generic-exception': errorMessages.join(' ') };

      return;
    }

    try {
      yield this.reloadProjectdigest.perform(projectdigest.id);

      if (this.args.model.needsWorkSample) {
        this.applicationStateManager.addToast('Project successfully upgraded to work sample.');
      }
    } catch (e) {
      this.applicationStateManager.addToast('Work sample updated successfully but it failed to reload. Please refresh the page.');
    }

    // close overlay upon successful save
    this.handleCloseSecondaryScreen();
  }

  @restartableTask
  *handleVerifyDigestTask(projectdigest, verificationData) {
    try {
      yield this.projectdigestCommandService.createVerification(projectdigest, verificationData);
    } catch (e) {
      handleFormErrorInOverlay(
        this,
        e,
        VERIFICATION_OVERLAY_FORM_INPUTS,
        'unhandled-exception',
        null,
        'verifyScreenErrors',
      );
      this.errorTracker.error('Failed to submit verification form', this, e);
      return;
    }

    try {
      yield this.reloadProjectdigest.perform(projectdigest.id);
    } catch (e) {
      this.applicationStateManager.addToast('Failed to reload. Please refresh the page.');
      return;
    }

    // close overlay upon successful save
    this.handleCloseSecondaryScreen();
  }

  @dropTask
  *loadFacebookPlayerSDK() {
    // only initisalize once once
    if (this.facebookSDKInitialized) {
      return;
    }

    try {
      yield this.facebookProviderService.load();

      this.disableFacebookPlayer = false;
      this.facebookSDKInitialized = true;
    } catch (error) {
      // use default player instead
      this.disableFacebookPlayer = true;
      this.errorTracker.error('Facebook player failed to initialize', this, error);
    }
  }

  @restartableTask
  *reloadProjectdigest(id) {
    try {
      const response = yield this.projectdigestCommandService.getProjectDigest(id, PROFILE_PROJECTDIGEST_INCLUDES);
      this.shData.pushResponseToStore(response);
      return this.store.peekRecord('projectdigest', response.data.id);
    } catch (error) {
      this.errorTracker.error(`reloadProjectdigest - failed to reload projectdigest id: ${id}`, this, error);
      throw error;
    }
  }
}

export default GlmLightboxComponent;
